import React from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceDeliveryNotification } from '@cp-mx/common';
import { Notification, NotificationStatus } from '@cp-shared-19/frontend-ui';

import { useUpdateInvoiceDeliveryNotifications } from '../useUpdateInvoiceDeliveryNotifications';
import { closeInvoiceDeliveryNotification } from '../InvoiceDeliveryNotificationsSlice';

export const InvoiceDeliveryNotificationsUi: React.FC<{
    invoiceDeliveries?: InvoiceDeliveryNotification[];
}> = ({ invoiceDeliveries }) => {
    const { updateInvoiceDeliveryNotifications } = useUpdateInvoiceDeliveryNotifications();

    const { t } = useTranslation('invoice-delivery-notifications');

    if (!invoiceDeliveries?.length) {
        return null;
    }

    /**
     * This function updates invoice delivery notifications once onClose action was triggered
     * @param contractNumber string
     */
    const handleOnClose = (contractNumber: string) => {
        const newNotifications = invoiceDeliveries.map(invoiceDelivery =>
            invoiceDelivery.contractNumber === contractNumber
                ? { ...invoiceDelivery, notificationClosed: true }
                : invoiceDelivery,
        );
        closeInvoiceDeliveryNotification(contractNumber);
        updateInvoiceDeliveryNotifications(newNotifications);
    };

    /**
     * This function creates notification title
     * @param contractNumber string
     * @returns {string}
     */
    const replaceHeadValues = (contractNumber: string): string => {
        return t('headline').replace('<contractNumber>', contractNumber);
    };

    /**
     * This function creates notification content
     * @param invoiceDeliveryNotification InvoiceDeliveryNotification
     * @returns {string}
     */
    const replaceContentValues = (invoiceDeliveryNotification: InvoiceDeliveryNotification): string => {
        const { dealerFullAddress, dealerName, invoiceDeliveryDate } = invoiceDeliveryNotification;

        return t('content')
            .replace('<invoiceDeliveryDate>', invoiceDeliveryDate as string)
            .replace('<dealershipName>', dealerName)
            .replace('<dealershipFullAddress>', dealerFullAddress);
    };

    /**
     * This function creates a notification element
     * @param invoiceDeliveryNotification InvoiceDeliveryNotification
     * @returns {JSX.Element}
     */
    const renderDefaultNotification = (invoiceDeliveryNotification: InvoiceDeliveryNotification): JSX.Element => {
        const { contractNumber, notificationClosed } = invoiceDeliveryNotification;

        return (
            <Notification
                status={NotificationStatus.success}
                key={`${contractNumber}`}
                showCloseButton
                visible={!notificationClosed}
                onCloseClick={() => handleOnClose(contractNumber)}
                headline={replaceHeadValues(contractNumber)}
                testId={'testIdDefault'}
            >
                <p>{replaceContentValues(invoiceDeliveryNotification)}</p>
                <p>{t('bottom')}</p>
            </Notification>
        );
    };

    return <>{invoiceDeliveries.map(invoiceDelivery => renderDefaultNotification(invoiceDelivery))}</>;
};
